<template>
    <div>
        <div class="alert alert-danger mb-3 text-center" role="alert" v-if="showError">
            {{ $t('checkout.cart_summary_error') }}
            <button v-if="redirect" class="btn btn-sm btn-outline-light ms-2" @click="redirectToCart">
                {{ $t('checkout.return_to_cart') }}
            </button>
            <button v-else class="btn btn-sm btn-outline-light ms-2" @click="retry">
                {{ $t('checkout.retry') }}
            </button>
        </div>

        <div
            class="card checkout-card checkout-card-payment-totals"
            v-if="!showError"
            :style="isUpdating ? cartUpdatingStyle : cartIdleStyle"
        >
            <div class="card-body">
                <h3 class="mb-3">{{ $t('checkout.payment_total_amount') }}</h3>

                <div class="label-value-row">
                    <span class="label">{{ $t('checkout.payment_total') }}</span>
                    <span class="value fw-bold">&euro;{{ formatNumber(positionPrice) }}</span>
                </div>

                <div class="label-value-row">
                    <span class="label">{{ $t('checkout.payment_delivery_cost') }}</span>
                    <span v-if="hasShippingCosts" class="value fw-bold">
                        &euro;{{ formatNumber(shippingCosts) }}
                    </span>
                    <span v-else class="value fw-bold">
                        -
                    </span>
                </div>

                <div v-if="discounts && discounts.length > 0" class="label-value-row text-success">
                    <span class="label">{{ $t('checkout.payment_discounts') }}</span>
                    <span class="value fw-bold">
                        -&euro;{{ formatNumber(totalDiscounts) }}
                    </span>
                </div>

                <hr />

                <div class="label-value-row">
                    <span class="label">{{ $t('checkout.payment_final_total') }}</span>
                    <span class="value fw-bold">
                        &euro;{{ formatNumber(totalPrice) }}
                    </span>
                </div>

                <div v-if="isTaxIncluded" class="label-value-row small text-muted">
                    <span class="label">{{ $t('checkout.payment_tax_included') }}</span>
                    <span class="value">&euro;{{ formatNumber(taxAmount) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import shopware from '../../mixins/shopware';
import formatters from '../../mixins/formatters';
import { EventBus } from '../../api/eventBus.ts';

/**
 * CartSummary operates in one of two modes:
 *
 *  - Managed (default, used inside Cart.vue / CheckoutInformation.vue):
 *      Parent component owns the cart and supplies it via the `cart` prop.
 *      This component does NOT fetch and does NOT listen to cart-updated;
 *      the parent is responsible for keeping the prop in sync with mutations.
 *      This eliminates a class of races where an in-flight independent fetch
 *      from this component would resolve AFTER a cart-updated event and clobber
 *      the fresh post-mutation cart with stale pre-mutation data (see
 *      voucher-application bug where shipping cost stayed at the pre-voucher
 *      value because CartSummary's own fetchCart resolved last).
 *
 *  - Standalone (opt-in via `:standalone="true"`, used in Blade pages with no
 *      Vue cart parent like information.blade.php / login_or_register.blade.php):
 *      This component owns its cart state, fetches it itself, and listens to
 *      cart-updated. A fetch generation counter guards against the same race
 *      within this component (any cart-updated event invalidates pending fetches).
 */
export default {
    name: 'CartSummary',
    mixins: [shopware, formatters],
    props: {
        cart: {
            type: Object,
            default: null
        },
        redirect: {
            type: String,
            default: ''
        },
        standalone: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            internalCart: null,
            cartFetchGen: 0,
            updatingQuantityItems: false,
            updatingShippingMethod: false,
            showError: false,
            retryCount: 0,
            maxRetries: 3
        };
    },
    created: function () {
        EventBus.$on('updating-quantity-items', this.updateQuantityItems);
        EventBus.$on('updating-shipping-method', this.updateShippingMethod);

        if (this.standalone) {
            // Standalone: own the cart lifecycle.
            EventBus.$on('cart-updated', this.handleCartUpdate);
            this.fetchCart();
        }
        // Managed: parent provides cart via prop and handles cart-updated; nothing to wire up here.
    },
    beforeDestroy() {
        EventBus.$off('updating-quantity-items', this.updateQuantityItems);
        EventBus.$off('updating-shipping-method', this.updateShippingMethod);

        if (this.standalone) {
            EventBus.$off('cart-updated', this.handleCartUpdate);
        }
    },
    computed: {
        cartData() {
            // Single source of truth per mode — never blend prop and internal state.
            return this.standalone ? this.internalCart : this.cart;
        },
        promotionItems() {
            const items = this.cartData?.lineItems || [];
            return items.filter(item =>
                item.type === 'promotion' || (item.good === false && item.price && item.price.totalPrice < 0)
            );
        },
        discounts() {
            return this.promotionItems.map(item => ({
                name: item.label,
                amount: Math.abs(item.price.totalPrice),
            }));
        },
        totalDiscounts() {
            return this.promotionItems.reduce(
                (sum, item) => sum + Math.abs(item.price.totalPrice), 0
            );
        },
        positionPrice() {
            // Shopware's positionPrice already includes promotion deductions; add them back for true subtotal
            return (this.cartData?.price?.positionPrice || 0) + this.totalDiscounts;
        },
        shippingCosts() {
            return this.cartData?.deliveries?.[0]?.shippingCosts?.totalPrice || 0;
        },
        totalPrice() {
            return this.cartData?.price?.totalPrice || 0;
        },
        taxAmount() {
            return this.cartData?.price?.calculatedTaxes?.[0]?.tax || 0;
        },
        hasShippingCosts() {
            return this.shippingCosts > 0;
        },
        isTaxIncluded() {
            return this.taxAmount > 0;
        },
        // True while a sibling component (ShipmentSelector / CartLineItems) is awaiting its server response.
        // Drives a unified dim on the whole summary card so the user sees one atomic update when the cart-updated event lands.
        isUpdating() {
            return this.updatingQuantityItems || this.updatingShippingMethod;
        },
        cartUpdatingStyle() {
            return {
                opacity: 0.5,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease'
            };
        },
        cartIdleStyle() {
            return {
                transition: 'opacity 0.15s ease'
            };
        }
    },
    methods: {
        retry() {
            this.showError = false;
            if (this.standalone) {
                this.fetchCart();
            }
        },

        redirectToCart() {
            window.location.href = this.redirect;
        },

        handleCartUpdate(cart) {
            // Only invoked in standalone mode — see created().
            if (cart) {
                // Invalidate any in-flight fetchCart so its stale resolution cannot clobber this fresh state.
                this.cartFetchGen++;
                this.internalCart = cart;
            }
        },

        async fetchCart() {
            const fetchGen = ++this.cartFetchGen;
            try {
                const cart = await this.api.client.invoke('readCart get /checkout/cart');
                // Race guard: a cart-updated event (or a newer fetchCart) may have arrived while we were awaiting.
                if (fetchGen === this.cartFetchGen) {
                    this.internalCart = cart;
                }
            } catch (error) {
                console.error('Error fetching cart:', error);

                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    await this.fetchCart();
                } else {
                    this.showError = true;
                    this.retryCount = 0;

                    if (this.redirect) {
                        setTimeout(() => {
                            window.location.href = this.redirect;
                        }, 3000);
                    }
                }
            }
        },

        updateQuantityItems(status) {
            this.updatingQuantityItems = status;
        },

        updateShippingMethod(status) {
            this.updatingShippingMethod = status;
        }
    }
};
</script>
